import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, ProviderAccreditationPendenciesService, ProviderAccreditationStatusService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HPMSOnClickTermUse, HpmsTermServiceApi } from '@philips-emr/hpms-provider-accreditation-components';
import { ButtonAPI, FetchAPI, FieldAPI, FormAPI, ModalAPI, PanelAPI, RepeaterAPI } from '@philips-emr/tws-components';
import Controller, { OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationConclusionAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationStatus } from './provider-accreditation-controller';

interface DataTerm {
  nameTerm: string;
  term: string;
}

interface Response {
  response: string,
  status: string,
}

interface CancellationReason {
  nrSequenceCancellationReason: number,
  dsCancellationReason: string,
}

const PENDENCIES = 'P';
@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION_CONCLUSION })
export default class ProviderAccreditationConclusionController extends BaseController {
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;

  @Inject(Aliases.PANEL_TERM)
  private panelTerm: PanelAPI;
  @Inject(Aliases.TERM_FORM_PROVIDER_ACCREDITATION)
  private termFormProvider: HpmsTermServiceApi;
  @Inject(Aliases.FIELD_TERM)
  private fieldTerm: FieldAPI;
  @Inject(Aliases.MODAL_TERM_FORM)
  private modalTerm: ModalAPI;
  @Inject(Aliases.BUTTON_SEND)
  private buttonSend: ButtonAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.PANEL_LOADER_PENDENCIES)
  private panelLoaderPendencies: PanelAPI;
  @Inject(Aliases.REPEATER_PENDENCIES)
  private repeater: RepeaterAPI;
  @Inject(Aliases.PANEL_CANCEL)
  private panelCancelRequest: PanelAPI;
  @Inject(Aliases.FORM_CANCEL_REQUEST)
  private formCancelRequest: FormAPI;
  @Inject(Aliases.BUTTON_CANCEL_REQUEST)
  private buttonCancelRequest: ButtonAPI;

  @OnMount(Aliases.PANEL_CONCLUSION)
  onMountPanelConclusion(): void {
    const { nrSeqTypeAccreditation, nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void HttpService.get(Endpoint.GET.TYPE_ACCREDITATION, { nrSeqTypeAccreditation, nrSeqUser }).then(resp => {
      const data = resp.body as string;
      if (data.length == 0) {
        this.fieldTerm.setVisible(false);
      }
    });

    void ProviderAccreditationPendenciesService.getPendencies(nrSeqAccreditation as number, nrSeqUser as number, PENDENCIES).then((response) => {
      catchRenderException(() => {
        if (isEmptyArrayOrObject(response.body)) {
          this.panelPendencies.setVisible(false);
        } else {
          this.buttonSend.setEnabled(false);
        }
      });
    });
  }

  @OnMount(Aliases.REPEATER_PENDENCIES)
  onMountRepeater(): void {
    const { nrSeqAccreditation, nrSeqUser } = this.historyService.getCurrentRouteParams();

    void ProviderAccreditationPendenciesService.getPendencies(nrSeqAccreditation as number, nrSeqUser as number, 'P').then((response) => {
      catchRenderException(() => {
        if (!isEmptyArrayOrObject(response.body)) {
          this.repeater.setData(response.body);
        }
        this.panelLoaderPendencies.setVisible(false);
      });
    });
  }

  @OnMount([Aliases.BUTTON_SEND, Aliases.BUTTON_CANCEL_REQUEST])
  onMountButtom(event: MountEvent): void {
    const buttom = event.target as ButtonAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqUser as number).then((response) => {
      const { status } = response.body;
      this.controlVisibilityButtons(buttom, status);
    });
  }

  private controlVisibilityButtons(buttom: ButtonAPI, status: string): void {
    if (buttom.getAlias() === Aliases.BUTTON_CANCEL_REQUEST) {
      buttom.setVisible(status === AccreditationStatus.AWAITING_REQUESTER);
    } else {
      buttom.setEnabled(status === AccreditationStatus.AWAITING_REQUESTER || status == AccreditationStatus.AWAITING_REVISION);
    }
  }

  @OnMount(Aliases.BUTTON_CANCEL_REQUEST)
  onMountButtomCancel(event: MountEvent): void {
    const buttom = event.target as ButtonAPI;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqUser as number)
      .then((response) => {
        const { status } = response.body;
        buttom.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER);
      }).catch(() => ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request')));
  }

  @OnMount(Aliases.FETCH_FINAL_MESSAGE)
  asynconMountFetch(event: MountEvent): void {
    const api = (event.target as unknown) as FetchAPI;
    const { nrSeqUser } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqUser });
    api.forceFetch();
  }

  @HPMSOnClickTermUse(Aliases.HPMS_TERM_CHECKBOX)
  onClickTermCheckbox(): void {
    const { nrSeqTypeAccreditation, nrSeqUser } = this.historyService.getCurrentRouteParams();
    void HttpService.get(Endpoint.GET.TYPE_ACCREDITATION, { nrSeqTypeAccreditation, nrSeqUser }).then(resp => {
      const { nameTerm } = resp.body as DataTerm;
      const data = resp.body as string;
      this.panelTerm.setVisible(true);
      this.termFormProvider.setData(data);
      this.panelTerm.setVisible(true);
      this.modalTerm.setTitle(nameTerm);
    });
  }

  @OnClick(Aliases.BUTTON_SEND)
  onClickButtonSend(): void {
    this.panelLoader.setVisible(true);
    this.buttonCancelRequest.setVisible(false);
    this.buttonSend.setEnabled(false);
    const { nrSeqAccreditation, nrSeqTypeAccreditation, nrSeqUser } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.ACCREDITATION_SEND, { nrSeqUser, nrSeqAccreditation, nrSeqTypeAccreditation })
      .then((resp) => {
        this.buttonCancelRequest.setVisible(false);
        this.buttonSend.setEnabled(false);
        const { response } = resp.body as Response;
        ToastServiceProviderAccreditation.success(response);
        this.panelLoader.setVisible(false);
      })
      .catch((resp) => {
        this.buttonCancelRequest.setVisible(true);
        this.buttonSend.setEnabled(true);
        const { response } = resp.body as unknown as Response;
        ToastServiceProviderAccreditation.error(response);
        this.panelLoader.setVisible(false);
      });
  }

  @OnClose(Aliases.MODAL_TERM_FORM)
  closeModal(): void {
    this.panelTerm.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_CANCEL_REQUEST)
  onClickCancel(): void {
    this.panelCancelRequest.setVisible(true);
  }

  @OnClose(Aliases.MODAL_REASON_FOR_CANCELING_ACCREDITATION)
  onCloseModalCancel(): void {
    this.panelCancelRequest.setVisible(false);
  }

  @OnCancel(Aliases.FORM_CANCEL_REQUEST)
  onCancelForm(): void {
    this.panelCancelRequest.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_CANCEL_REQUEST)
  onSubmitFormCancelRequest(event: FormAPI): void {
    const { record } = event;
    const { nrSeqAccreditation, nrSeqUser } = this.historyService.getCurrentRouteParams();
    const { cancellationReason } = record;
    const { nrSequenceCancellationReason } = cancellationReason as CancellationReason;
    this.formCancelRequest.setEditing(false);
    this.formCancelRequest.setLoading(true);
    HttpService.post(Endpoint.POST.CANCELING_ACCREDITATION, { nrSeqUser, nrSeqAccreditation, nrSequenceCancellationReason })
      .then((resp) => {
        const message = resp.body as string;
        ToastServiceProviderAccreditation.success(message);
        this.formCancelRequest.setLoading(false);
        this.panelCancelRequest.setVisible(false);
        this.buttonCancelRequest.setVisible(false);
      })
      .catch(() => {
        this.formCancelRequest.setLoading(false);
        this.formCancelRequest.setEditing(true);
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
        this.buttonCancelRequest.setVisible(true);
      });
  }

}
